import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes } from 'react-icons/fa';
import { DiGithubBadge } from 'react-icons/di';
import avatar from '../assets/mlsvd.png';

const navLinks = [
  { to: '/', label: 'Home', exact: true },
  { to: '/profile', label: 'Profile' },
  { to: '/snippets', label: 'Snippets' },
  { to: '/projects', label: 'Projects' },
  { to: '/articles', label: 'Articles' },
  { to: 'https://github.com/mlsvd', label: 'GitHub', external: true },
  { to: '/contact', label: 'Contact' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Nav>
      <NavInner>
        <LogoLink to="/">
          <LogoAvatar src={avatar} alt="Dmitri Malisev" />
        </LogoLink>

        <DesktopLinks>
          {navLinks.map(link =>
            link.external ? (
              <NavA key={link.to} href={link.to} target="_blank" rel="noopener noreferrer">
                <DiGithubBadge size={15} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                {link.label}
              </NavA>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                $active={link.exact ? location.pathname === link.to : location.pathname.startsWith(link.to)}
              >
                {link.label}
              </NavLink>
            )
          )}
        </DesktopLinks>

        <MobileToggle onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
          {menuOpen ? <FaTimes size={17} /> : <FaBars size={17} />}
        </MobileToggle>
      </NavInner>

      <div className={menuOpen ? 'mobile-menu-open show' : 'mobile-menu-open'}>
        {navLinks.map(link =>
          link.external ? (
            <a key={link.to} href={link.to} target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>
          ) : (
            <Link key={link.to} to={link.to}>{link.label}</Link>
          )
        )}
        <CloseHint onClick={() => setMenuOpen(false)}>close</CloseHint>
      </div>
    </Nav>
  );
};

const Nav = styled.nav`
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-primary);
`;

const NavInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const LogoLink = styled(Link)`
  text-decoration: none;
`;

const LogoAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const DesktopLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.75rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--text-primary)' : 'var(--text-muted)'};
  text-decoration: none;
  transition: color 0.15s;

  &:hover {
    color: var(--text-primary);
  }
`;

const NavA = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-muted);
  text-decoration: none;
  transition: color 0.15s;

  &:hover {
    color: var(--text-primary);
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const CloseHint = styled.button`
  margin-top: 2rem;
  background: none;
  border: 1px solid var(--border);
  color: var(--text-muted);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  padding: 0.375rem 1rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

export default Header;
